import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../../lib/api';
import { useFavorites } from '../../stores/favorites';
import { useSession } from '../../stores/session';

/**
 * Fill in the heart for one track that arrived without one.
 *
 * ## Why anything needs this
 *
 * Every list carries `favorited` with each row, so the heart in a list is right
 * the moment it renders. A room does not, and cannot: its state is pushed over
 * the socket to every listener at once, so nothing in that payload can be true
 * for one of them and false for another. Put `favorited` there and you
 * broadcast the host's answer to the whole room.
 *
 * The result was three hearts that were always empty - the live section, the
 * dock and the host console all passed `favorited={false}` because there was
 * nothing else to pass, so a track you had favourited months ago looked
 * unfavourited every time it came on.
 *
 * This asks the one question those surfaces cannot answer from what they were
 * given, and puts it in the store every heart already reads. Nothing renders
 * differently for having called it; the button picks the answer up on its own.
 *
 * ## Once per track, not once per render
 *
 * Keyed on the id and never refetched on focus. The answer only changes when
 * this person changes it, and when they do the toggle writes the new value into
 * the same store - so a refetch could only ever confirm what is already there,
 * or briefly contradict an optimistic update that has not landed yet.
 *
 * ## Signed out it does nothing
 *
 * The answer is always no, which the client knows without asking, and the
 * endpoint requires a session. `enabled` keeps it from firing a request that
 * would 401 on every track change of every broadcast a visitor listens to.
 */
export function useTrackFavorite(trackId: number | undefined): void {
  const user = useSession((state) => state.user);
  const prime = useFavorites((state) => state.prime);

  const { data } = useQuery({
    queryKey: ['track-favorite', trackId],
    queryFn: () => api.get<{ favorited: boolean; like_count: number }>(`/tracks/${trackId}/like`),
    enabled: user !== null && trackId !== undefined,
    staleTime: Infinity,
    refetchOnWindowFocus: false,

    /*
     * One attempt. A heart that cannot be read is drawn empty, which is what it
     * would have been anyway - retrying spends requests to fix the cosmetic
     * half of a feature that still works when you click it.
     */
    retry: false,
  });

  useEffect(() => {
    if (data === undefined || trackId === undefined) return;

    /*
     * Only ever fills a blank.
     *
     * The store's rule is that the overlay is what this session has changed and
     * it wins over anything the server said - so writing over it here would
     * undo a click. That is not hypothetical: the request goes out when the
     * track changes, and somebody who hits the heart in the second before it
     * lands would watch it flip back off on its own.
     *
     * Read through `getState` rather than by subscribing, because this is a
     * question about the moment the answer arrives, and subscribing to the
     * overlay would re-run this effect on every favourite anywhere in the app.
     */
    if (useFavorites.getState().overlay[trackId] !== undefined) return;

    prime(trackId, data.favorited, data.like_count);
  }, [data, trackId, prime]);
}
